import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function AdminLoanRequests() {
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [filter, setFilter] = useState('pre_approved');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/api/admin/loan-requests');
      setAllRequests(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = filter 
    ? allRequests.filter(r => r.status === filter)
    : allRequests;

  const handleApprove = async (requestId, requestData) => {
    if (!confirm(`Aprovar sol·licitud de ${requestData.lender_name}?\\n\\nEs crearà un préstec de ${requestData.requested_amount.toLocaleString()}€`)) {
      return;
    }

    setProcessing(requestId);
    try {
      const response = await api.post(`/api/admin/loan-requests/${requestId}/approve`, {
        amount: requestData.requested_amount,
        interest_rate: requestData.suggested_interest_rate,
        duration_months: 12
      });
      
      toast.success(`Sol·licitud aprovada! Préstec creat.`);
      navigate(`/admin/contracts/edit/${response.data.loan_id}`);
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || 'Error'));
    } finally {
      setProcessing(null);
      fetchRequests();
    }
  };

  const handleReject = async (requestId, lenderName, amount) => {
    const reason = prompt(`Rebutjar ${lenderName} (${amount.toLocaleString()}€)?\\n\\nMotiu (s'enviarà per email):`);
    if (reason === null) return;

    setProcessing(requestId);
    try {
      await api.delete(`/api/admin/loan-requests/${requestId}`, {
        data: { rejection_reason: reason }
      });
      toast.success('Rebutjada. Email enviat amb motiu.');
      fetchRequests();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || 'Error'));
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (requestId, lenderName) => {
    if (!confirm(`ELIMINAR ${lenderName}?\\n\\nAquesta acció no es pot desfer.`)) {
      return;
    }

    setProcessing(requestId);
    try {
      await api.post(`/api/admin/loan-requests/${requestId}/delete-permanent`);
      toast.success('Eliminada');
      fetchRequests();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || 'Error'));
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('loanRequests.title')} />

        <main style={{ padding: '2.5rem' }} className="fade-in">
          {/* Filters */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            {[
              { value: 'pre_approved', label: 'Pre-aprovades', count: allRequests.filter(r => r.status === 'pre_approved').length, color: '#FF9800' },
              { value: 'approved', label: 'Aprovades', count: allRequests.filter(r => r.status === 'approved').length, color: '#007a33' },
              { value: 'rejected', label: 'Rebutjades', count: allRequests.filter(r => r.status === 'rejected').length, color: '#ef4444' },
              { value: '', label: 'Totes', count: allRequests.length, color: '#103766' }
            ].map(({ value, label, count, color }) => (
              <button
                key={value}
                onClick={() => setFilter(value)}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '12px',
                  background: filter === value ? color : 'rgba(255,255,255,0.05)',
                  color: filter === value ? 'white' : 'rgba(255,255,255,0.7)',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {label} ({count})
              </button>
            ))}
          </div>

          {filteredRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', background: 'rgba(255,255,255,0.03)', borderRadius: '20px' }}>
              <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</p>
              <h3 style={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.9)' }}>
                No hi ha sol·licituds en aquesta categoria
              </h3>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {filteredRequests.map((request) => (
                <div key={request._id} className="dark-card" style={{ padding: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                    <div>
                      <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white' }}>{request.lender_name}</h3>
                      <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.5)' }}>
                        {new Date(request.created_at).toLocaleDateString('ca-ES')}
                      </p>
                    </div>
                    <span style={{
                      padding: '0.5rem 1rem',
                      background: request.status === 'pre_approved' ? 'rgba(255,152,0,0.2)' : request.status === 'approved' ? 'rgba(0,122,51,0.2)' : 'rgba(239,68,68,0.2)',
                      color: request.status === 'pre_approved' ? '#FF9800' : request.status === 'approved' ? '#007a33' : '#ef4444',
                      borderRadius: '20px',
                      fontSize: '0.8125rem',
                      fontWeight: '600'
                    }}>
                      {request.status === 'pre_approved' ? 'Pre-aprovada' : request.status === 'approved' ? 'Aprovada' : 'Rebutjada'}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div style={{ padding: '1.5rem', background: 'rgba(59,130,246,0.1)', borderRadius: '12px' }}>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>IMPORT</p>
                      <p style={{ fontSize: '2rem', fontWeight: '700', color: '#60a5fa' }}>
                        {request.requested_amount.toLocaleString()}€
                      </p>
                    </div>
                    <div style={{ padding: '1.5rem', background: 'rgba(16,185,129,0.1)', borderRadius: '12px' }}>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>INTERÈS</p>
                      <p style={{ fontSize: '2rem', fontWeight: '700', color: '#34d399' }}>
                        {request.suggested_interest_rate}%
                      </p>
                    </div>
                  </div>

                  {request.rejection_reason && (
                    <div style={{ padding: '1rem', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', marginBottom: '1rem', borderLeft: '3px solid #ef4444' }}>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>MOTIU:</p>
                      <p style={{ fontSize: '0.875rem', color: '#ef4444' }}>{request.rejection_reason}</p>
                    </div>
                  )}

                  {request.status === 'pre_approved' && (
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        onClick={() => handleApprove(request._id, request)}
                        disabled={processing === request._id}
                        style={{ flex: 1, padding: '1rem', background: 'linear-gradient(135deg, #007a33 0%, #00a843 100%)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: processing === request._id ? 'not-allowed' : 'pointer' }}
                      >
                        {processing === request._id ? '⏳ Processant...' : '✓ Aprovar'}
                      </button>
                      <button
                        onClick={() => handleReject(request._id, request.lender_name, request.requested_amount)}
                        disabled={processing === request._id}
                        style={{ padding: '1rem 2rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        ✕ Rebutjar
                      </button>
                    </div>
                  )}

                  {(request.status === 'rejected' || request.status === 'approved') && (
                    <button
                      onClick={() => handleDelete(request._id, request.lender_name)}
                      disabled={processing === request._id}
                      style={{ width: '100%', padding: '0.875rem', background: 'rgba(97,97,97,0.1)', border: '1px solid rgba(97,97,97,0.3)', color: '#9ca3af', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      {processing === request._id ? '⏳ Eliminant...' : '🗑️ Eliminar'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminLoanRequests;
